<!--
  InitialsTimeField — the paper's "SCI Initials / Time" column.

  The spec's attribution rule: there are no per-person accounts, so the shift
  name chosen at session start is what stamps an entry. This control therefore
  offers a one-tap fill from the shift name rather than making a gloved thumb
  type initials, and a one-tap "Now" for the clock time.

  `timeEditable = false` is used where the server stamps `recorded_at` (ITP and
  job-setup items); the time is then shown read-only so the record still reads
  like the paper column.
-->
<script lang="ts">
  interface Props {
    /** Column heading, e.g. 'SCI initials / time' or 'Tester'. */
    label?: string;
    initials: string | null;
    /** 'HH:MM' or an ISO-8601 timestamp. */
    time: string | null;
    /** Session shift name — powers the one-tap initials fill. */
    shiftName?: string | null;
    /** false when the server stamps the time (ITP recorded_at). */
    timeEditable?: boolean;
    timeLabel?: string;
    disabled?: boolean;
    compact?: boolean;
    hideLabel?: boolean;
    onInitials?: (v: string | null) => void;
    onTime?: (v: string | null) => void;
    /** Fired on blur of either input — the screen flushes the section here. */
    onCommit?: () => void;
  }

  let {
    label = 'SCI initials / time',
    initials,
    time,
    shiftName = null,
    timeEditable = true,
    timeLabel = 'Time',
    disabled = false,
    compact = false,
    hideLabel = false,
    onInitials,
    onTime,
    onCommit
  }: Props = $props();

  const id = `it-${Math.random().toString(36).slice(2, 9)}`;

  /** 'Geoff Horsey' -> 'GH'. Single word -> first two letters. */
  function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return parts
      .map((p) => p[0])
      .join('')
      .slice(0, 4)
      .toUpperCase();
  }

  let suggested = $derived(shiftName ? initialsOf(shiftName) : '');

  /** ISO-8601 or 'HH:MM' -> 'HH:MM' for the time input. */
  function toHHMM(v: string | null): string {
    if (!v) return '';
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  function nowHHMM(): string {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  function norm(raw: string): string | null {
    return raw.trim() === '' ? null : raw.trim();
  }

  function stampNow() {
    if (disabled) return;
    onTime?.(nowHHMM());
    onCommit?.();
  }

  function fillInitials() {
    if (disabled || !suggested) return;
    onInitials?.(suggested);
    onCommit?.();
  }
</script>

<div class="itf" class:compact>
  {#if !hideLabel}<span class="label">{label}</span>{/if}
  <div class="row">
    <div class="cell">
      <input
        id="{id}-ini"
        class="initials"
        type="text"
        inputmode="text"
        autocapitalize="characters"
        autocomplete="off"
        spellcheck="false"
        maxlength="6"
        placeholder="INI"
        aria-label="Initials"
        {disabled}
        value={initials ?? ''}
        oninput={(e) => onInitials?.(norm((e.currentTarget as HTMLInputElement).value.toUpperCase()))}
        onblur={() => onCommit?.()}
      />
      {#if suggested && !disabled}
        <button type="button" class="quick" onclick={fillInitials} title="Fill from shift name">
          {suggested}
        </button>
      {/if}
    </div>

    <div class="cell">
      {#if timeEditable}
        <input
          id="{id}-time"
          class="time"
          type="time"
          aria-label={timeLabel}
          {disabled}
          value={toHHMM(time)}
          oninput={(e) => onTime?.(norm((e.currentTarget as HTMLInputElement).value))}
          onblur={() => onCommit?.()}
        />
        {#if !disabled}
          <button type="button" class="quick" onclick={stampNow} title="Stamp the current time">
            Now
          </button>
        {/if}
      {:else}
        <output class="time-static" aria-label={timeLabel}>{toHHMM(time) || '—'}</output>
      {/if}
    </div>
  </div>
</div>

<style>
  .itf {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
    line-height: 1.2;
  }

  .row {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
    flex-wrap: wrap;
  }

  .cell {
    display: flex;
    gap: 0.25rem;
    align-items: stretch;
    flex: 1 1 8rem;
    min-width: 0;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    /* 16px minimum so iOS Safari does not zoom on focus. */
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    min-height: 3rem;
    padding: 0.5rem 0.625rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  .compact input {
    min-height: 2.75rem;
    padding: 0.375rem 0.5rem;
  }

  .initials {
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .time {
    font-variant-numeric: tabular-nums;
  }

  input:focus {
    outline: none;
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  input:disabled {
    background: var(--surface-disabled, #eef1f4);
    color: var(--fg-muted, #3f4750);
    -webkit-text-fill-color: var(--fg-muted, #3f4750);
    opacity: 1;
  }

  .quick {
    flex: 0 0 auto;
    min-height: 3rem;
    min-width: 3rem;
    padding: 0 0.5rem;
    font-size: 0.875rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    border: 2px solid var(--accent, #0b63c5);
    border-radius: 0.5rem;
    background: var(--accent-bg, #e8f1fb);
    color: var(--accent, #0b63c5);
    cursor: pointer;
    touch-action: manipulation;
  }

  .compact .quick {
    min-height: 2.75rem;
    min-width: 2.75rem;
  }

  .quick:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .time-static {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-height: 3rem;
    padding: 0.5rem 0.625rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: var(--fg, #10151b);
    background: var(--surface-muted, #eef1f4);
    border: 2px solid transparent;
    border-radius: 0.5rem;
  }
</style>
